import React, { useEffect, useState } from "react";
import { Shield, Truck, X, Clock, Zap, Package } from "lucide-react";
import "./OrderSummary.css";

const OrderSummary = ({
  // Props for cart data
  totalItems,
  totalAmount,
  totalTax,
  deliveryCharge,
  deliveryLoading,
  deliveryError,
  deliveryInfo,

  // Coupon system props
  coupons = [],
  selectedCoupon,
  setSelectedCoupon,
  applyCoupon,
  couponMessage,
  couponLoading = false,
  onRemoveCoupon,

  // Calculation helpers
  getTaxBreakdown,
  getDiscountAmount,
  calculateFinalTotal,

  // Actions
  onPlaceOrder,
  onContinueShopping,
  onProceedToPayment,
  loading,

  // Additional UI options
  showActions = true,
  showTrustBadges = true,
  buttonText = "Place Order",
  className = "",
}) => {
  const [displayMessage, setDisplayMessage] = useState(couponMessage);

  const discount = getDiscountAmount();
  const finalTotal = calculateFinalTotal();
  const taxBreakdown = getTaxBreakdown();
  const totalTaxAmount = Object.values(taxBreakdown).reduce((sum, tax) => sum + tax, 0);

  // ============ AUTO HIDE MESSAGE EFFECT ============
  useEffect(() => {
    if (couponMessage) {
      setDisplayMessage(couponMessage);
      const timer = setTimeout(() => {
        setDisplayMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setDisplayMessage("");
    }
  }, [couponMessage]);

  // ============ COUPON HANDLERS ============
  const handleCouponSelect = (e) => {
    const selectedCode = e.target.value;
    setSelectedCoupon(selectedCode);
  };

  const handleRemoveCoupon = () => {
    setSelectedCoupon("");
    setDisplayMessage("");
    if (onRemoveCoupon) {
      onRemoveCoupon();
    }
  };

  return (
    <div className={`order-summary-container ${className}`}>
      <div className="order-summary">
        {/* HEADER */}
        <div className="summary-header">
          <h2>Order Summary</h2>
        </div>

        {/* ✅ DELIVERY INFO - COMPACT */}
        {deliveryInfo && !deliveryError && !deliveryLoading && (
          <div className="delivery-info-compact">
            <div className="delivery-compact-row">
              <Clock size={16} className="delivery-icon" />
              <span className="delivery-text">
                {deliveryInfo.estimatedDeliveryTime || `${deliveryInfo.duration} mins`}
              </span>
            </div>
            
            {deliveryInfo.isSameDayDelivery && (
              <div className="same-day-compact">
                <Zap size={14} />
                <span>Same Day</span>
              </div>
            )}
          </div>
        )}

        {/* PRICE BREAKDOWN */}
        <div className="summary-details">
          {/* ITEMS */}
          <div className="summary-row">
            <span className="row-label">
              <Package size={16} />
              Items ({totalItems})
            </span>
            <span className="row-value">₹{totalAmount.toFixed(2)}</span>
          </div>

          {/* DELIVERY */}
          <div className="summary-row">
            <span className="row-label">
              <Truck size={16} />
              Delivery
            </span>
            <span className={`row-value ${deliveryCharge === 0 ? 'free-text' : ''}`}>
              {deliveryLoading
                ? "Calculating..."
                : deliveryError
                ? "—"
                : deliveryCharge === 0
                ? "FREE"
                : `₹${deliveryCharge}`}
            </span>
          </div>

          {/* TAX - COMBINED */}
          {totalTaxAmount > 0 && (
            <div className="summary-row">
              <span className="row-label">Taxes & Fees</span>
              <span className="row-value">₹{Math.round(totalTaxAmount)}</span>
            </div>
          )}

          {/* DISCOUNT */}
          {discount > 0 && (
            <div className="summary-row discount-row">
              <span className="row-label">Savings</span>
              <span className="row-value savings">-₹{discount}</span>
            </div>
          )}

          <div className="summary-divider"></div>

          {/* TOTAL */}
          <div className="summary-total">
            <span className="total-label">Total</span>
            <span className="total-amount">₹{finalTotal}</span>
          </div>
        </div>

        {/* COUPON SECTION - COMPACT */}
        <div className="coupon-compact">
          <div className="coupon-header-compact">
            <span>🎫 Have a coupon?</span>
          </div>
          
          {!selectedCoupon || !couponMessage.includes("✓") ? (
            <div className="coupon-input-compact">
              <select
                value={
                  typeof selectedCoupon === "object"
                    ? selectedCoupon?.code
                    : selectedCoupon || ""
                }
                onChange={handleCouponSelect}
                className="coupon-select-compact"
                disabled={couponLoading}
              >
                <option value="">Select a coupon</option>
                {coupons.map((coupon) => (
                  <option key={coupon._id} value={coupon.code}>
                    {coupon.code} - {coupon.discountPercentage}% off
                  </option>
                ))}
              </select>
              
              <button
                onClick={applyCoupon}
                className="coupon-apply-compact"
                disabled={couponLoading || !selectedCoupon}
              >
                {couponLoading ? "..." : "Apply"}
              </button>
            </div>
          ) : (
            <div className="applied-coupon-compact">
              <span className="applied-code">
                {typeof selectedCoupon === "object" ? selectedCoupon.code : selectedCoupon}
              </span>
              <button onClick={handleRemoveCoupon} className="remove-compact">
                <X size={14} />
              </button>
            </div>
          )}

          {/* MESSAGE */}
          {displayMessage && (
            <div className={`coupon-message-compact ${displayMessage.includes("✓") ? "success" : "error"}`}>
              {displayMessage}
            </div>
          )}
        </div>

        {/* ACTION BUTTON */}
        {showActions && (onPlaceOrder || onProceedToPayment) && (
          <button
            onClick={onProceedToPayment || onPlaceOrder}
            className="btn-checkout-compact"
            disabled={loading || totalItems === 0 || deliveryLoading}
          >
            {deliveryLoading ? "Calculating..." : buttonText}
          </button>
        )}

        {/* TRUST BADGES - MINIMAL */}
        {showTrustBadges && (
          <div className="trust-badges-compact">
            <div className="trust-item">
              <Shield size={14} />
              <span>Secure Payment</span>
            </div>
            <div className="trust-item">
              <Truck size={14} />
              <span>Fast Delivery</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderSummary; 